import React from "react";
import WebProfitSpinner from "../../system/WebProfitSpinner";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import HourglassTopRoundedIcon from "@mui/icons-material/HourglassTopRounded";
import LaunchRoundedIcon from "@mui/icons-material/LaunchRounded";
import PaymentsRoundedIcon from "@mui/icons-material/PaymentsRounded";
import RadioButtonUncheckedRoundedIcon from "@mui/icons-material/RadioButtonUncheckedRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import SettingsRoundedIcon from "@mui/icons-material/SettingsRounded";
import VisibilityOffRoundedIcon from "@mui/icons-material/VisibilityOffRounded";
import { integrationsApi } from "../../../api/integrations";
import { useProjectSettingsDirty } from "../ProjectSettingsUnsavedContext";
import { PAYMENT_PROVIDER_LINKS } from "../../../utils/paymentProviderLinks";

const BRAND = { orange: "#F35A3A", dark: "var(--wp-panel-text-primary, #19242F)" };
const STEPS = ["Inicio", "Payway", "Ventas Online", "MODO", "Validación", "Resultado"];

const EMPTY_INTEGRATION = {
  provider: "modo",
  enabled: false,
  connected: false,
  connectionStatus: "not_configured",
  mode: "production",
  validatedMode: "",
  hasModoUsername: false,
  modoUsernamePreview: "",
  hasModoPassword: false,
  processorCode: "",
  ccCode: "",
  payway: {
    ecommerceEstablishmentNumber: "",
    siteId: "",
    publicKey: "",
    hasPrivateKey: false,
    privateKeyRetained: false,
  },
  onboarding: {
    path: "",
    currentStep: 0,
    externalPending: "",
  },
  checklist: {},
  lastTestedAt: null,
  lastSuccessfulSyncAt: null,
  lastError: "",
  lastErrorCode: "",
};

function formatDate(value) {
  if (!value) return "Todavía no se validó";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat("es-AR", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
}

function formFromIntegration(integration = EMPTY_INTEGRATION) {
  return {
    onboardingPath: integration?.onboarding?.path || "",
    mode: integration?.mode || "production",
    paywayEstablishmentNumber: integration?.payway?.ecommerceEstablishmentNumber || "",
    paywaySiteId: integration?.payway?.siteId || "",
    paywayPublicKey: integration?.payway?.publicKey || "",
    paywayPrivateKey: "",
    modoUsername: "",
    modoPassword: "",
    processorCode: integration?.processorCode || "",
    ccCode: integration?.ccCode || "",
  };
}

function displayStatus(integration) {
  if (integration?.connected && integration?.connectionStatus === "connected") {
    if (integration.mode === "test") return { label: "Conectado en pruebas", color: "warning" };
    if (!integration.enabled) return { label: "Desactivado", color: "default" };
    return { label: "Conectado", color: "success" };
  }
  if (integration?.connectionStatus === "external_pending") {
    return { label: "Pendiente de habilitación externa", color: "warning" };
  }
  if (integration?.connectionStatus === "error") {
    return { label: "Error de configuración", color: "error" };
  }
  if (integration?.connectionStatus === "incomplete") {
    return { label: "Configuración incompleta", color: "info" };
  }
  return { label: "No configurado", color: "default" };
}

function actionLabel(integration) {
  if (integration?.connectionStatus === "error") return "Revisar error";
  if (integration?.connected) return "Administrar MODO";
  if (["incomplete", "external_pending"].includes(integration?.connectionStatus)) {
    return "Continuar configuración";
  }
  return "Configurar MODO";
}

function pendingDescription(integration) {
  const pending = integration?.onboarding?.externalPending;
  if (pending === "payway_establishment") return "Esperando el alta o el establecimiento e-commerce de Payway.";
  if (pending === "payway_online") return "Esperando la habilitación o los datos de Payway Ventas Online.";
  if (pending === "modo_credentials") return "Esperando las credenciales MODO v2.";
  return "Hay una gestión externa pendiente antes de continuar.";
}

function resumeStep(integration) {
  if (integration?.connected && integration?.connectionStatus === "connected") return 5;
  if (integration?.connectionStatus === "error" && integration?.hasModoPassword) return 4;
  const pending = integration?.onboarding?.externalPending;
  if (pending === "payway_establishment") return 1;
  if (pending === "payway_online") return 2;
  if (pending === "modo_credentials") return 3;
  const stored = Number(integration?.onboarding?.currentStep || 0);
  if (integration?.onboarding?.path === "credentials_ready" && stored < 3) return 3;
  return Math.max(0, Math.min(5, stored));
}

function checklistRows(integration) {
  return [
    ["paywayEstablishment", "Establecimiento e-commerce Payway"],
    ["paywayOnline", "Payway Ventas Online / Site ID"],
    ["paywayCredentials", "Credenciales Payway disponibles"],
    ["modoCredentials", "Credenciales MODO v2 recibidas"],
    ["modoConnection", "Conexión MODO validada"],
    ["productionEnabled", "Producción habilitada"],
  ].map(([key, label]) => ({ key, label, ...(integration?.checklist?.[key] || {}) }));
}

function ExternalButton({ href, children, ...props }) {
  return (
    <Button
      component="a"
      href={href}
      target="_blank"
      rel="noreferrer"
      aria-label={typeof children === "string" ? `${children} (abre en una pestaña nueva)` : undefined}
      title="Abre en una pestaña nueva"
      endIcon={<LaunchRoundedIcon />}
      {...props}
    >
      {children}
    </Button>
  );
}

function Checklist({ integration }) {
  const rows = checklistRows(integration);
  return (
    <Stack spacing={1}>
      {rows.map((row) => {
        const validated = row.verification === "webprofit_validated";
        return (
          <Stack
            key={row.key}
            direction="row"
            spacing={1.25}
            alignItems="flex-start"
            sx={{ p: 1.25, border: "1px solid", borderColor: "divider", borderRadius: 2.5 }}
          >
            {row.complete ? (
              <CheckCircleRoundedIcon color={validated ? "success" : "primary"} fontSize="small" />
            ) : (
              <RadioButtonUncheckedRoundedIcon color="disabled" fontSize="small" />
            )}
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 800 }}>{row.label}</Typography>
              <Typography variant="caption" color="text.secondary">
                {row.complete
                  ? validated ? "Validado por WebProfit" : "Completado por el comercio"
                  : "Pendiente"}
              </Typography>
            </Box>
          </Stack>
        );
      })}
    </Stack>
  );
}

export default function ModoIntegrationCard({ projectId, projectName, canManage }) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [integration, setIntegration] = React.useState(EMPTY_INTEGRATION);
  const [form, setForm] = React.useState(() => formFromIntegration(EMPTY_INTEGRATION));
  const [step, setStep] = React.useState(0);
  const [open, setOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [testing, setTesting] = React.useState(false);
  const [changingEnabled, setChangingEnabled] = React.useState(false);
  const [error, setError] = React.useState("");
  const [message, setMessage] = React.useState("");

  const applyIntegration = React.useCallback((value) => {
    const next = {
      ...EMPTY_INTEGRATION,
      ...(value || {}),
      payway: { ...EMPTY_INTEGRATION.payway, ...(value?.payway || {}) },
      onboarding: { ...EMPTY_INTEGRATION.onboarding, ...(value?.onboarding || {}) },
      checklist: value?.checklist || {},
    };
    setIntegration(next);
    setForm(formFromIntegration(next));
    return next;
  }, []);

  const load = React.useCallback(async () => {
    if (!projectId) return;
    try {
      setLoading(true);
      setError("");
      const data = await integrationsApi.getModo(projectId);
      applyIntegration(data.integration);
    } catch (requestError) {
      setError(requestError?.message || "No se pudo cargar el estado de MODO.");
    } finally {
      setLoading(false);
    }
  }, [projectId, applyIntegration]);

  React.useEffect(() => {
    load();
  }, [load]);

  const modoDirty = React.useMemo(() => {
    if (loading) return false;
    const baseline = formFromIntegration(integration);
    return Object.keys(baseline).some((key) => String(form[key] ?? "") !== String(baseline[key] ?? ""));
  }, [loading, integration, form]);
  useProjectSettingsDirty("ecommerce-modo", modoDirty);

  const change = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const basePayload = React.useCallback((overrides = {}) => {
    const payload = {
      onboardingPath: form.onboardingPath,
      currentStep: step,
      mode: form.mode,
      paywayEstablishmentNumber: form.paywayEstablishmentNumber,
      paywaySiteId: form.paywaySiteId,
      paywayPublicKey: form.paywayPublicKey,
      processorCode: form.processorCode,
      ccCode: form.ccCode,
      ...overrides,
    };
    if (form.paywayPrivateKey.trim()) payload.paywayPrivateKey = form.paywayPrivateKey.trim();
    if (form.modoUsername.trim()) payload.modoUsername = form.modoUsername.trim();
    if (form.modoPassword.trim()) payload.modoPassword = form.modoPassword.trim();
    return payload;
  }, [form, step]);

  const save = async ({ overrides = {}, closeAfter = false, nextStep = null } = {}) => {
    if (!canManage) return null;
    try {
      setSaving(true);
      setError("");
      setMessage("");
      const data = await integrationsApi.saveModo(projectId, basePayload(overrides));
      const next = applyIntegration(data.integration);
      const resolvedStep = nextStep === null ? resumeStep(next) : nextStep;
      setStep(resolvedStep);
      setMessage(data.message || "Guardamos el avance de MODO.");
      if (closeAfter) setOpen(false);
      return next;
    } catch (requestError) {
      if (requestError?.data?.integration) applyIntegration(requestError.data.integration);
      setError(requestError?.message || "No se pudo guardar el avance de MODO.");
      return null;
    } finally {
      setSaving(false);
    }
  };

  const openWizard = () => {
    setError("");
    setMessage("");
    setForm(formFromIntegration(integration));
    setStep(resumeStep(integration));
    setOpen(true);
  };

  const goForward = async () => {
    if (!canManage || saving || testing) return;

    if (step === 0) {
      if (!form.onboardingPath) {
        setError("Elegí si empezás desde cero o si ya tenés credenciales MODO v2.");
        return;
      }
      const directCredentials = form.onboardingPath === "credentials_ready";
      const target = directCredentials ? 3 : 1;
      await save({
        overrides: {
          currentStep: target,
          externalPending: "",
          ...(directCredentials
            ? {
                checklist: {
                  paywayEstablishmentComplete: true,
                  paywayOnlineComplete: true,
                  paywayCredentialsComplete: true,
                },
              }
            : {}),
        },
        nextStep: target,
      });
      return;
    }

    if (step === 1) {
      if (!String(form.paywayEstablishmentNumber || "").trim()) {
        setError("Ingresá el número de establecimiento e-commerce o guardá este paso como pendiente.");
        return;
      }
      await save({
        overrides: {
          currentStep: 2,
          externalPending: "",
          checklist: { paywayEstablishmentComplete: true },
        },
        nextStep: 2,
      });
      return;
    }

    if (step === 2) {
      const hasPrivateKey = Boolean(form.paywayPrivateKey.trim() || integration?.payway?.hasPrivateKey);
      if (!form.paywaySiteId.trim() || !form.paywayPublicKey.trim() || !hasPrivateKey) {
        setError("Completá Site ID, API Key Pública y API Key Privada de Payway, o guardá el paso como pendiente.");
        return;
      }
      await save({
        overrides: {
          currentStep: 3,
          externalPending: "",
          checklist: {
            paywayOnlineComplete: true,
            paywayCredentialsComplete: true,
          },
        },
        nextStep: 3,
      });
      return;
    }

    if (step === 3) {
      const hasUsername = Boolean(form.modoUsername.trim() || integration?.hasModoUsername);
      const hasPassword = Boolean(form.modoPassword.trim() || integration?.hasModoPassword);
      if (!hasUsername || !hasPassword || !form.processorCode.trim() || !form.ccCode.trim()) {
        setError("Completá las credenciales MODO v2 y la configuración comercial antes de continuar.");
        return;
      }
      if (!/^[A-Za-z0-9._-]+$/.test(form.processorCode.trim()) || !/^[A-Za-z0-9._-]+$/.test(form.ccCode.trim())) {
        setError("Processor code y condición comercial sólo pueden contener letras, números, punto, guion o guion bajo.");
        return;
      }
      await save({
        overrides: {
          currentStep: 4,
          externalPending: "",
          checklist: { modoCredentialsComplete: true },
        },
        nextStep: 4,
      });
    }
  };

  const goBack = () => {
    setError("");
    if (step === 3 && form.onboardingPath === "credentials_ready") {
      setStep(0);
      return;
    }
    setStep((current) => Math.max(0, current - 1));
  };

  const markPending = async (kind) => {
    await save({
      overrides: { externalPending: kind, currentStep: step },
      closeAfter: true,
      nextStep: step,
    });
  };

  const testConnection = async () => {
    if (!canManage) return;
    try {
      setTesting(true);
      setError("");
      setMessage("");
      const saved = await integrationsApi.saveModo(projectId, basePayload({
        currentStep: 4,
        externalPending: "",
      }));
      applyIntegration(saved.integration);
      const data = await integrationsApi.testModo(projectId);
      const next = applyIntegration(data.integration);
      setStep(5);
      setMessage(data.message || "Conexión MODO verificada.");
      return next;
    } catch (requestError) {
      if (requestError?.data?.integration) {
        const next = applyIntegration(requestError.data.integration);
        setStep(next?.connectionStatus === "error" ? 4 : resumeStep(next));
      }
      setError(requestError?.message || "No pudimos verificar MODO.");
      return null;
    } finally {
      setTesting(false);
    }
  };

  const enable = async () => {
    try {
      setChangingEnabled(true);
      setError("");
      const data = await integrationsApi.enableModo(projectId);
      applyIntegration(data.integration);
      setMessage(data.message || "MODO quedó activado.");
    } catch (requestError) {
      if (requestError?.data?.integration) applyIntegration(requestError.data.integration);
      setError(requestError?.message || "No se pudo activar MODO.");
    } finally {
      setChangingEnabled(false);
    }
  };

  const disable = async () => {
    if (!window.confirm("¿Desactivar MODO? La configuración y el historial se conservarán.")) return;
    try {
      setChangingEnabled(true);
      setError("");
      const data = await integrationsApi.disableModo(projectId);
      applyIntegration(data.integration);
      setMessage(data.message || "MODO quedó desactivado.");
    } catch (requestError) {
      setError(requestError?.message || "No se pudo desactivar MODO.");
    } finally {
      setChangingEnabled(false);
    }
  };

  const startProduction = () => {
    change("mode", "production");
    setError("");
    setStep(4);
  };

  const rows = checklistRows(integration);
  const completed = rows.filter((row) => row.complete).length;
  const state = displayStatus(integration);
  const busy = saving || testing || changingEnabled;

  const renderStep = () => {
    if (step === 0) {
      return (
        <Stack spacing={2.5}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 950 }}>¿En qué situación estás?</Typography>
            <Typography variant="body2" color="text.secondary">
              Para cobrar con MODO necesitás procesamiento online habilitado y credenciales MODO v2. WebProfit te guía aunque empieces desde cero.
            </Typography>
          </Box>
          <Grid container spacing={1.5}>
            <Grid item xs={12} md={6}>
              <Button
                fullWidth
                variant={form.onboardingPath === "from_scratch" ? "contained" : "outlined"}
                onClick={() => change("onboardingPath", "from_scratch")}
                disabled={!canManage || busy}
                sx={{ minHeight: 96, justifyContent: "flex-start", textAlign: "left", textTransform: "none", borderRadius: 3, px: 2 }}
              >
                <Box>
                  <Typography sx={{ fontWeight: 950 }}>Empiezo desde cero</Typography>
                  <Typography variant="caption">Payway → Ventas Online → MODO v2</Typography>
                </Box>
              </Button>
            </Grid>
            <Grid item xs={12} md={6}>
              <Button
                fullWidth
                variant={form.onboardingPath === "credentials_ready" ? "contained" : "outlined"}
                onClick={() => change("onboardingPath", "credentials_ready")}
                disabled={!canManage || busy}
                sx={{ minHeight: 96, justifyContent: "flex-start", textAlign: "left", textTransform: "none", borderRadius: 3, px: 2 }}
              >
                <Box>
                  <Typography sx={{ fontWeight: 950 }}>Ya tengo credenciales MODO v2</Typography>
                  <Typography variant="caption">Ir directo a carga y validación</Typography>
                </Box>
              </Button>
            </Grid>
          </Grid>
          <Alert severity="info">
            Elegir un camino sólo organiza el onboarding. Si indicás que ya tenés credenciales MODO v2, los pasos externos de Payway se registran como completados por vos, no como validados por WebProfit. MODO no se considera conectado hasta la prueba real.
          </Alert>
        </Stack>
      );
    }

    if (step === 1) {
      return (
        <Stack spacing={2.25}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 950 }}>1. Establecimiento e-commerce de Payway</Typography>
            <Typography variant="body2" color="text.secondary">
              Payway procesa la operación detrás de este recorrido. Necesitás un número de establecimiento e-commerce activo; si ya usás Payway, podés buscarlo en tus establecimientos.
            </Typography>
          </Box>
          <TextField
            fullWidth
            label="Número de establecimiento e-commerce"
            value={form.paywayEstablishmentNumber}
            onChange={(event) => change("paywayEstablishmentNumber", event.target.value)}
            disabled={!canManage || busy}
            inputProps={{ inputMode: "numeric", maxLength: 32 }}
            helperText="Usá el número de establecimiento e-commerce informado por Payway."
          />
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            <ExternalButton href={PAYMENT_PROVIDER_LINKS.paywaySignup} variant="outlined" sx={{ textTransform: "none", fontWeight: 850 }}>
              Ir al alta de Payway
            </ExternalButton>
            {canManage ? (
              <Button
                variant="text"
                startIcon={<HourglassTopRoundedIcon />}
                onClick={() => markPending("payway_establishment")}
                disabled={busy}
                sx={{ textTransform: "none", fontWeight: 850 }}
              >
                Guardar como pendiente y salir
              </Button>
            ) : null}
          </Stack>
          <Alert severity="info">El alta ocurre fuera de WebProfit. Podés volver más tarde sin perder este avance.</Alert>
        </Stack>
      );
    }

    if (step === 2) {
      return (
        <Stack spacing={2.25}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 950 }}>2. Payway Ventas Online (Decidir Plus)</Typography>
            <Typography variant="body2" color="text.secondary">
              Con el establecimiento activo, necesitás Site ID, API Key Pública y API Key Privada. Cargarlos registra que completaste el alta externa; todavía no significa que MODO esté validado.
            </Typography>
          </Box>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            <ExternalButton href={PAYMENT_PROVIDER_LINKS.paywayOnlineOnboarding} variant="outlined" sx={{ textTransform: "none", fontWeight: 850 }}>
              Crear Payway Ventas Online
            </ExternalButton>
            <ExternalButton href={PAYMENT_PROVIDER_LINKS.paywayOnlinePortal} variant="text" sx={{ textTransform: "none", fontWeight: 850 }}>
              Ya tengo cuenta: abrir portal
            </ExternalButton>
          </Stack>
          <TextField
            fullWidth
            label="Site ID"
            value={form.paywaySiteId}
            onChange={(event) => change("paywaySiteId", event.target.value)}
            disabled={!canManage || busy}
            helperText="Identificador del sitio dentro de Payway Ventas Online."
          />
          <TextField
            fullWidth
            label="API Key Pública de Payway"
            value={form.paywayPublicKey}
            onChange={(event) => change("paywayPublicKey", event.target.value)}
            disabled={!canManage || busy}
            helperText="Dato no secreto que acompaña tu configuración de Ventas Online."
          />
          <TextField
            fullWidth
            label="API Key Privada de Payway"
            type="password"
            value={form.paywayPrivateKey}
            onChange={(event) => change("paywayPrivateKey", event.target.value)}
            disabled={!canManage || busy}
            placeholder={integration?.payway?.hasPrivateKey ? "Configurada; dejá vacío para conservarla" : "Pegá la clave privada"}
            helperText={integration?.payway?.hasPrivateKey
              ? "La clave actual está cifrada y no vuelve al navegador."
              : "Se cifra en backend. Cuando MODO v2 queda validado, WebProfit deja de conservarla si no la necesita en runtime."}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end"><VisibilityOffRoundedIcon fontSize="small" /></InputAdornment>
              ),
            }}
          />
          {canManage ? (
            <Button
              variant="text"
              startIcon={<HourglassTopRoundedIcon />}
              onClick={() => markPending("payway_online")}
              disabled={busy}
              sx={{ alignSelf: "flex-start", textTransform: "none", fontWeight: 850 }}
            >
              Estoy esperando la habilitación o los datos
            </Button>
          ) : null}
        </Stack>
      );
    }

    if (step === 3) {
      return (
        <Stack spacing={2.25}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 950 }}>3. Credenciales MODO v2</Typography>
            <Typography variant="body2" color="text.secondary">
              MODO v2 utiliza usuario, contraseña, configuración del procesador (processor_code) y condición comercial/cuotas (cc_code). No necesitás pegar un access token ni usar Store ID como contrato principal.
            </Typography>
          </Box>
          <ExternalButton href={PAYMENT_PROVIDER_LINKS.modoCredentialsV2} variant="outlined" sx={{ alignSelf: "flex-start", textTransform: "none", fontWeight: 850 }}>
            Ver cómo obtener credenciales MODO v2
          </ExternalButton>
          <TextField
            fullWidth
            label="Usuario MODO"
            value={form.modoUsername}
            onChange={(event) => change("modoUsername", event.target.value)}
            disabled={!canManage || busy}
            placeholder={integration?.hasModoUsername ? `Configurado (${integration.modoUsernamePreview || "oculto"}); dejá vacío para conservarlo` : "Usuario recibido de MODO"}
            helperText="Si ya existe uno guardado, no lo mostramos completo. Escribí otro sólo si querés reemplazarlo."
          />
          <TextField
            fullWidth
            label="Contraseña MODO"
            type="password"
            value={form.modoPassword}
            onChange={(event) => change("modoPassword", event.target.value)}
            disabled={!canManage || busy}
            placeholder={integration?.hasModoPassword ? "Configurada; dejá vacío para conservarla" : "Contraseña recibida de MODO"}
            helperText="Se cifra en backend y nunca vuelve completa al navegador."
            InputProps={{
              endAdornment: (
                <InputAdornment position="end"><VisibilityOffRoundedIcon fontSize="small" /></InputAdornment>
              ),
            }}
          />
          <Grid container spacing={1.5}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Configuración del procesador"
                value={form.processorCode}
                onChange={(event) => change("processorCode", event.target.value)}
                disabled={!canManage || busy}
                placeholder="Ej. P1000"
                helperText="Referencia técnica: processor_code. Usá el valor enviado por MODO."
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Opciones de cuotas / financiación"
                value={form.ccCode}
                onChange={(event) => change("ccCode", event.target.value)}
                disabled={!canManage || busy}
                placeholder="Ej. 1CSI"
                helperText="Referencia técnica: cc_code. Debe coincidir con una condición comercial soportada."
              />
            </Grid>
          </Grid>
          <ExternalButton href={PAYMENT_PROVIDER_LINKS.modoCommercialConditions} variant="text" sx={{ alignSelf: "flex-start", textTransform: "none", fontWeight: 800 }}>
            Consultar condiciones comerciales oficiales
          </ExternalButton>
          {canManage ? (
            <Button
              variant="text"
              startIcon={<HourglassTopRoundedIcon />}
              onClick={() => markPending("modo_credentials")}
              disabled={busy}
              sx={{ alignSelf: "flex-start", textTransform: "none", fontWeight: 850 }}
            >
              Todavía estoy esperando las credenciales MODO
            </Button>
          ) : null}
        </Stack>
      );
    }

    if (step === 4) {
      return (
        <Stack spacing={2.25}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 950 }}>4. Entorno y validación real</Typography>
            <Typography variant="body2" color="text.secondary">
              WebProfit va a autenticar tus credenciales directamente con MODO desde el backend. No se crea ninguna compra para esta prueba.
            </Typography>
          </Box>
          <TextField
            select
            fullWidth
            label="Entorno"
            value={form.mode}
            onChange={(event) => change("mode", event.target.value)}
            disabled={!canManage || busy}
          >
            <MenuItem value="test">Pruebas / preproducción</MenuItem>
            <MenuItem value="production">Producción</MenuItem>
          </TextField>
          <Alert severity={form.mode === "test" ? "warning" : "info"}>
            {form.mode === "test"
              ? "Las credenciales de prueba se verifican contra preproducción. Aunque la prueba sea exitosa, MODO no se mostrará en el checkout público."
              : "Las credenciales productivas se verifican contra Producción. Después de una prueba exitosa, la activación del medio de pago queda como una acción separada."}
          </Alert>
          {integration?.lastError ? (
            <Alert severity={integration?.lastErrorCode === "MODO_INVALID_CREDENTIALS" ? "error" : "warning"}>
              {integration.lastError}
              {integration?.lastTestedAt ? ` Última prueba: ${formatDate(integration.lastTestedAt)}.` : ""}
            </Alert>
          ) : null}
          <Button
            variant="contained"
            onClick={testConnection}
            disabled={!canManage || busy}
            startIcon={testing ? <WebProfitSpinner size={18} color="inherit" /> : <RefreshRoundedIcon />}
            sx={{ alignSelf: "flex-start", textTransform: "none", fontWeight: 900, borderRadius: 999, px: 3, bgcolor: BRAND.orange }}
          >
            {testing ? "Validando credenciales con MODO…" : "Probar conexión"}
          </Button>
          <Typography variant="caption" color="text.secondary">
            Un cambio de usuario, contraseña, processor_code, cc_code o entorno invalida la validación anterior y requiere probar otra vez.
          </Typography>
        </Stack>
      );
    }

    return (
      <Stack spacing={2.25}>
        <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={1.5} alignItems={{ sm: "center" }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 950 }}>Tu integración MODO</Typography>
            <Typography variant="body2" color="text.secondary">Estado operativo y accesos para administrar la conexión.</Typography>
          </Box>
          <Chip label={displayStatus(integration).label} color={displayStatus(integration).color} sx={{ fontWeight: 900, alignSelf: { xs: "flex-start", sm: "center" } }} />
        </Stack>

        {integration?.connected && integration?.mode === "test" ? (
          <Alert severity="warning">
            Esta conexión está validada sólo en pruebas. No se mostrará en el checkout público hasta que cargues y valides credenciales de Producción.
          </Alert>
        ) : null}
        {integration?.connectionStatus === "error" && integration?.lastError ? (
          <Alert severity="error">
            {integration.lastError}{integration?.lastTestedAt ? ` Última prueba: ${formatDate(integration.lastTestedAt)}.` : ""}
          </Alert>
        ) : null}
        {integration?.connectionStatus === "connected" && integration?.lastError ? (
          <Alert severity="warning">
            La última revalidación no pudo completarse, pero conservamos la última conexión válida. {integration.lastError}
          </Alert>
        ) : null}
        {integration?.connected && integration?.mode === "production" && !integration?.enabled ? (
          <Alert severity="info">Las credenciales de Producción están verificadas. MODO sigue desactivado hasta que lo actives explícitamente. Si pasó tiempo desde la última validación, podés probar de nuevo antes de reactivarlo.</Alert>
        ) : null}
        {integration?.connected && integration?.mode === "production" && integration?.enabled ? (
          <Alert severity="success">MODO está conectado y habilitado para este proyecto.</Alert>
        ) : null}

        <Grid container spacing={1.5}>
          <Grid item xs={12} sm={6}>
            <Box sx={{ p: 1.5, border: "1px solid", borderColor: "divider", borderRadius: 2.5 }}>
              <Typography variant="caption" color="text.secondary">Entorno</Typography>
              <Typography sx={{ fontWeight: 900 }}>{integration?.mode === "test" ? "Pruebas / preproducción" : "Producción"}</Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Box sx={{ p: 1.5, border: "1px solid", borderColor: "divider", borderRadius: 2.5 }}>
              <Typography variant="caption" color="text.secondary">Última validación exitosa</Typography>
              <Typography sx={{ fontWeight: 900 }}>{formatDate(integration?.lastSuccessfulSyncAt)}</Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Box sx={{ p: 1.5, border: "1px solid", borderColor: "divider", borderRadius: 2.5 }}>
              <Typography variant="caption" color="text.secondary">Configuración del procesador</Typography>
              <Typography sx={{ fontWeight: 900 }}>{integration?.processorCode || "Pendiente"}</Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Box sx={{ p: 1.5, border: "1px solid", borderColor: "divider", borderRadius: 2.5 }}>
              <Typography variant="caption" color="text.secondary">Condición comercial</Typography>
              <Typography sx={{ fontWeight: 900 }}>{integration?.ccCode || "Pendiente"}</Typography>
            </Box>
          </Grid>
        </Grid>

        <Checklist integration={integration} />

        {canManage ? (
          <Stack spacing={1}>
            {integration?.connected && integration?.mode === "production" && !integration?.enabled ? (
              <Button variant="contained" onClick={enable} disabled={busy} sx={{ alignSelf: "flex-start", textTransform: "none", fontWeight: 900 }}>
                {changingEnabled ? "Activando…" : "Activar MODO"}
              </Button>
            ) : null}
            {integration?.connected && integration?.mode === "test" ? (
              <Button variant="contained" onClick={startProduction} disabled={busy} sx={{ alignSelf: "flex-start", textTransform: "none", fontWeight: 900 }}>
                Configurar producción
              </Button>
            ) : null}
            <Accordion elevation={0} disableGutters sx={{ border: "1px solid", borderColor: "divider", borderRadius: "12px !important", overflow: "hidden", "&:before": { display: "none" } }}>
              <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />} sx={{ minHeight: 44, "& .MuiAccordionSummary-content": { my: .75 } }}>
                <Typography variant="body2" sx={{ fontWeight: 900, color: BRAND.dark }}>Opciones del proveedor</Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0 }}>
                <Stack spacing={.5} sx={{ "& .MuiButton-root": { justifyContent: "flex-start", textTransform: "none", fontWeight: 850 } }}>
                  <Button startIcon={<RefreshRoundedIcon />} onClick={() => setStep(4)} disabled={busy}>Probar de nuevo</Button>
                  <Button startIcon={<SettingsRoundedIcon />} onClick={() => setStep(3)} disabled={busy}>Reemplazar credenciales</Button>
                  {integration?.enabled ? (
                    <Button color="error" onClick={disable} disabled={busy}>Desactivar MODO</Button>
                  ) : null}
                </Stack>
              </AccordionDetails>
            </Accordion>
          </Stack>
        ) : null}

        <Divider />
        <Box>
          <Typography sx={{ fontWeight: 900 }}>Operación y soporte</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.25 }}>
            Las ventas se confirman desde WebProfit según el flujo de pago. Payway Ventas Online es el portal externo para consultar y gestionar transacciones del canal online.
          </Typography>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1} flexWrap="wrap" useFlexGap>
            <ExternalButton href={PAYMENT_PROVIDER_LINKS.paywayOnlinePortal} variant="text" sx={{ textTransform: "none", fontWeight: 800 }}>Ver transacciones en Payway</ExternalButton>
            <ExternalButton href={PAYMENT_PROVIDER_LINKS.modoDocs} variant="text" sx={{ textTransform: "none", fontWeight: 800 }}>Documentación MODO</ExternalButton>
            <ExternalButton href={PAYMENT_PROVIDER_LINKS.modoSupport} variant="text" sx={{ textTransform: "none", fontWeight: 800 }}>Ayuda MODO</ExternalButton>
            <ExternalButton href={PAYMENT_PROVIDER_LINKS.paywaySupport} variant="text" sx={{ textTransform: "none", fontWeight: 800 }}>Soporte Payway</ExternalButton>
          </Stack>
        </Box>
      </Stack>
    );
  };

  return (
    <>
      <Card elevation={0} sx={{ borderRadius: 4, border: "1px solid rgba(var(--wp-panel-ink-rgb, 25,36,47),0.08)" }}>
        <CardContent sx={{ p: { xs: 2.5, md: 3.25 } }}>
          {loading ? (
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minHeight: 120 }}>
              <WebProfitSpinner size={22} />
              <Typography color="text.secondary">Cargando estado de MODO…</Typography>
            </Stack>
          ) : error && integration?.connectionStatus === "not_configured" ? (
            <Alert severity="error" action={<Button color="inherit" size="small" onClick={load}>Reintentar</Button>}>
              {error}
            </Alert>
          ) : (
            <Stack spacing={2.25}>
              <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={2} alignItems={{ md: "center" }}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Box sx={{ width: 46, height: 46, borderRadius: 3, display: "grid", placeItems: "center", bgcolor: "rgba(65,87,255,0.1)", color: "#3F51F5" }}>
                    <PaymentsRoundedIcon />
                  </Box>
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 950, color: BRAND.dark }}>MODO</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Cobros online con alta guiada mediante Payway y credenciales MODO v2.
                    </Typography>
                  </Box>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Chip label={state.label} color={state.color} sx={{ fontWeight: 900 }} />
                  {integration?.connectionStatus !== "not_configured" ? (
                    <Chip label={integration?.mode === "test" ? "Pruebas" : "Producción"} variant="outlined" sx={{ fontWeight: 850 }} />
                  ) : null}
                </Stack>
              </Stack>

              {integration?.connectionStatus === "external_pending" ? (
                <Alert severity="info">{pendingDescription(integration)} Guardamos el avance para que puedas retomar desde ese paso.</Alert>
              ) : null}
              {integration?.lastError && integration?.connectionStatus === "error" ? (
                <Alert severity="error" icon={<ErrorOutlineRoundedIcon />}>
                  {integration.lastError}{integration?.lastTestedAt ? ` Última prueba: ${formatDate(integration.lastTestedAt)}.` : ""}
                </Alert>
              ) : null}
              {integration?.lastError && integration?.connectionStatus === "connected" ? (
                <Alert severity="warning">La última revalidación no pudo completarse. Se conserva la última conexión válida.</Alert>
              ) : null}

              <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={1.5} alignItems={{ sm: "center" }}>
                <Box>
                  <Typography variant="body2" color="text.secondary">Progreso</Typography>
                  <Typography sx={{ fontWeight: 900 }}>{completed} de 6 pasos completados</Typography>
                </Box>
                <Button
                  variant={integration?.connectionStatus === "not_configured" ? "contained" : "outlined"}
                  onClick={openWizard}
                  disabled={loading}
                  startIcon={integration?.connected ? <SettingsRoundedIcon /> : <ArrowForwardRoundedIcon />}
                  sx={{ textTransform: "none", fontWeight: 900, borderRadius: 999, px: 2.5 }}
                >
                  {actionLabel(integration)}
                </Button>
              </Stack>
            </Stack>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={open}
        onClose={() => !busy && setOpen(false)}
        fullScreen={fullScreen}
        fullWidth
        maxWidth="md"
        PaperProps={{ sx: { minHeight: fullScreen ? "100dvh" : "min(760px, calc(100dvh - 48px))" } }}
      >
        <DialogTitle sx={{ pr: 7, pb: 1.5 }}>
          <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 900 }}>COBROS ONLINE · MODO</Typography>
          <Typography variant="h5" sx={{ fontWeight: 950 }}>Configurar MODO</Typography>
          <Typography variant="body2" color="text.secondary">{projectName || "Tu proyecto"}</Typography>
          <IconButton
            aria-label="Cerrar configuración MODO"
            onClick={() => setOpen(false)}
            disabled={busy}
            sx={{ position: "absolute", right: 12, top: 12 }}
          >
            <CloseRoundedIcon />
          </IconButton>
        </DialogTitle>

        {busy ? <WebProfitSpinner size={22} sx={{ mx: "auto", my: 0.75 }} /> : null}

        <DialogContent dividers sx={{ bgcolor: "var(--wp-panel-surface-soft, #F7F8FB)", p: { xs: 1.5, sm: 2.5 } }}>
          <Stack spacing={2.5}>
            {fullScreen ? (
              <Box>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.75 }}>
                  <Typography variant="body2" sx={{ fontWeight: 900 }} aria-current="step">Paso {step + 1} de {STEPS.length}</Typography>
                  <Typography variant="caption" color="text.secondary">{STEPS[step]}</Typography>
                </Stack>
                <LinearProgress variant="determinate" value={((step + 1) / STEPS.length) * 100} />
              </Box>
            ) : (
              <Box sx={{ overflowX: "auto", pb: 0.5 }}>
                <Stepper activeStep={step} sx={{ minWidth: 680 }}>
                  {STEPS.map((label, index) => (
                    <Step key={label} aria-current={index === step ? "step" : undefined}>
                      <StepLabel>{label}</StepLabel>
                    </Step>
                  ))}
                </Stepper>
              </Box>
            )}

            {error ? <Alert severity="error">{error}</Alert> : null}
            {message ? <Alert severity="success">{message}</Alert> : null}
            {!canManage ? <Alert severity="warning">Sólo owner o admin puede modificar y validar credenciales.</Alert> : null}

            <Box sx={{ bgcolor: "background.paper", borderRadius: 3, p: { xs: 1.5, sm: 2.5 }, border: "1px solid", borderColor: "divider" }}>
              {renderStep()}
            </Box>

            {step > 0 && step < 5 ? (
              <Box sx={{ bgcolor: "background.paper", borderRadius: 3, p: { xs: 1.5, sm: 2 }, border: "1px solid", borderColor: "divider" }}>
                <Typography sx={{ fontWeight: 900, mb: 1 }}>Checklist persistente</Typography>
                <Checklist integration={integration} />
              </Box>
            ) : null}
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 2.5, py: 1.75, justifyContent: "space-between", gap: 1, flexWrap: "wrap" }}>
          <Box>
            {canManage && step < 5 ? (
              <Button
                startIcon={<SaveRoundedIcon />}
                onClick={() => save({ overrides: { currentStep: step }, closeAfter: true, nextStep: step })}
                disabled={busy}
                sx={{ textTransform: "none", fontWeight: 800 }}
              >
                Guardar y salir
              </Button>
            ) : (
              <Button onClick={() => setOpen(false)} disabled={busy} sx={{ textTransform: "none" }}>Cerrar</Button>
            )}
          </Box>
          <Stack direction="row" spacing={1}>
            {step > 0 && step < 5 ? (
              <Button startIcon={<ArrowBackRoundedIcon />} onClick={goBack} disabled={busy} sx={{ textTransform: "none" }}>
                Atrás
              </Button>
            ) : null}
            {step <= 3 && canManage ? (
              <Button
                variant="contained"
                endIcon={<ArrowForwardRoundedIcon />}
                onClick={goForward}
                disabled={busy}
                sx={{ textTransform: "none", fontWeight: 900 }}
              >
                Continuar
              </Button>
            ) : null}
            {step === 5 ? (
              <Button variant="contained" onClick={() => setOpen(false)} disabled={busy} sx={{ textTransform: "none", fontWeight: 900 }}>
                Listo
              </Button>
            ) : null}
          </Stack>
        </DialogActions>
      </Dialog>
    </>
  );
}
